import { bil } from '@/lib/sectionDefs';

const opt = (v, id, en) => ({ v, id, en });

/** Theme-level display & motion options (site.theme). */
export const THEME_FIELDS = [
  { key: 'motion', label: bil('Animasi', 'Animation'), def: 'none', options: [opt('none', 'Mati', 'Off'), opt('subtle', 'Tenang', 'Subtle'), opt('dynamic', 'Dinamis', 'Dynamic')] },
  { key: 'radius', label: bil('Sudut', 'Corners'), def: 'rounded', options: [opt('sharp', 'Tajam', 'Sharp'), opt('rounded', 'Membulat', 'Rounded'), opt('pill', 'Pil', 'Pill')] },
  { key: 'shadow', label: bil('Bayangan', 'Shadows'), def: 'soft', options: [opt('none', 'Tanpa bayangan', 'None'), opt('soft', 'Lembut', 'Soft'), opt('bold', 'Tegas', 'Bold')] },
  { key: 'button_style', label: bil('Gaya tombol', 'Button style'), def: 'solid', options: [opt('solid', 'Solid', 'Solid'), opt('gradient', 'Gradien', 'Gradient'), opt('outline', 'Garis', 'Outline')] },
  { key: 'density', label: bil('Kerapatan section', 'Section spacing'), def: 'normal', options: [opt('compact', 'Padat', 'Compact'), opt('normal', 'Normal', 'Normal'), opt('airy', 'Lapang', 'Airy')] },
  { key: 'mode', label: bil('Mode', 'Mode'), def: 'light', options: [opt('light', 'Terang', 'Light'), opt('dark', 'Gelap', 'Dark')] },
];

/** Per-section motion override (section.style.motion). */
export const SECTION_MOTIONS = [opt('inherit', 'Ikuti tema', 'Follow theme'), opt('none', 'Tanpa animasi', 'No animation'), opt('fade-up', 'Muncul dari bawah', 'Fade up'), opt('fade-in', 'Memudar masuk', 'Fade in'), opt('slide-left', 'Geser dari kiri', 'Slide from left'), opt('slide-right', 'Geser dari kanan', 'Slide from right'), opt('zoom', 'Perbesar', 'Zoom in')];

export const HERO_VARIANTS = ['split', 'centered', 'overlay', 'mesh'];

const norm = (field, v) => (field.options.some((o) => o.v === v) ? v : field.def);
export const themeValue = (theme, key) => norm(THEME_FIELDS.find((f) => f.key === key), theme?.[key]);

export const themeAttrs = (theme) => Object.fromEntries(THEME_FIELDS.map((f) => [`data-${f.key === 'button_style' ? 'btn' : f.key}`, norm(f, theme?.[f.key])]));

export const themeColors = (theme) => {
  const dark = theme?.mode === 'dark';
  return {
    '--site-ink': dark ? '#F1F5F9' : '#0F172A',
    '--site-muted': dark ? '#A3B1C6' : '#475569',
    '--site-border': dark ? 'rgba(255,255,255,0.12)' : '#E5E7EB',
    '--site-soft': dark ? '#121a2e' : '#F8FAFC',
    '--site-card': dark ? '#182136' : '#FFFFFF',
    '--site-heading-font': `'${theme?.heading_font || theme?.font || 'Inter'}', Inter, system-ui, sans-serif`,
    background: dark ? '#0B1020' : (theme?.background || '#FFFFFF'),
  };
};

/** Effective reveal effect for a section: explicit override wins, else the theme preset. */
export const resolveMotion = (theme, section) => {
  const own = section?.style?.motion;
  if (own && own !== 'inherit') return own;
  return themeValue(theme, 'motion') === 'none' ? 'none' : 'fade-up';
};

export const splitWords = (s) => String(s || '').split(/[,\n|]/).map((w) => w.trim()).filter(Boolean);

const hexRgb = (hex) => {
  const h = String(hex || '').replace('#', '');
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (!/^[0-9a-fA-F]{6}$/.test(f)) return null;
  const n = parseInt(f, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const lum = ([r, g, b]) => (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

/** In dark mode, lift dark brand colours so text/icons stay readable on the dark background. */
export const readableColor = (hex, dark, fallback) => {
  const rgb = hexRgb(hex) || hexRgb(fallback);
  if (!dark || !rgb) return hex || fallback;
  const l = lum(rgb);
  if (l >= 0.42) return hex;
  const k = Math.min(0.75, (0.5 - l) * 1.6);
  const mix = rgb.map((c) => Math.round(c + (255 - c) * k));
  return `#${mix.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
};
